import { Typography as MuiTypography, type TypographyProps as MuiTypographyProps } from '@mui/material';
import { forwardRef, useCallback } from 'react';

export type TypographyProps<C extends React.ElementType = 'span'> = Omit<
  MuiTypographyProps<
    C,
    {
      component?: C;
    }
  >,
  'component'
> & {
  ellipsis?: boolean;
  component?: C;
};

const Component = <C extends React.ElementType = 'span'>(
  { ellipsis = false, sx, component, ...props }: TypographyProps<C>,
  ref: React.ForwardedRef<HTMLElement>
) => {
  const getEllipsisStyles = useCallback(() => {
    if (!ellipsis) return {};

    return {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };
  }, [ellipsis]);

  return (
    <MuiTypography
      ref={ref}
      component={component}
      sx={{
        ...getEllipsisStyles(),
        ...sx,
      }}
      {...props}
    />
  );
};

export const Typography = forwardRef(Component) as <C extends React.ElementType = 'span'>(
  props: TypographyProps<C>
) => JSX.Element;
